import type { OrderOptionDto } from '../dto/order';
import { sanitizeProductDisplayName } from '../utils/productDisplayNameSanitizer';
import type {
  AllocationHintMap,
  SharedTransportCargoItem,
} from './sharedTransportFormUtils';

// Row/form-value shapes shared between SharedTransportForm and its extracted
// section components. Kept out of the section components so both the
// orchestrator (which derives the rows) and the presentational sections (which
// render them) import one canonical definition.
export interface CargoTableRow extends SharedTransportCargoItem {
  key: string;
  orderNumber: string;
  availableQuantity: number;
  allocatedElsewhere: number;
}

export interface CargoItemFormValue {
  id?: string;
  quantity?: number | null;
  goodsValueTotal?: number | null;
  manualAllocatedTransportCost?: number | null;
  // Per-line package-based LDM overrides (editable in the cargo table). When
  // untouched they fall back to the cargo snapshot, then the product default.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  // F2: operator-entered LDM override for this line. No product-level
  // default exists (it is a TransportCargoItem-only column) — when untouched
  // it falls back to the cargo snapshot only, then packaging.
  manualLdm?: number | null;
}

export function buildCargoKey(orderId: string, productId: string): string {
  return `${orderId}:${productId}`;
}

/**
 * Transport order lines can still contain legacy names assembled from a
 * business name, a product code, technical variant id and dimensions. Keep
 * those values searchable in their source records, but never repeat them in
 * the operator-facing picker or cargo card. Prefer the canonical catalog name
 * when the API provides it and use the same sanitizer as product selectors.
 */
export function getTransportOrderProductDisplayName(
  product: OrderOptionDto['products'][number],
): string {
  const technicalValues = [
    product.id,
    product.productVariantId,
    product.productCode,
    product.productEan,
  ];

  for (const candidate of [product.catalogProductName, product.name]) {
    const displayName = sanitizeProductDisplayName(candidate, technicalValues);
    if (displayName) return displayName;
  }

  return 'Produkt';
}

export function buildCargoRows(
  selectedOrders: OrderOptionDto[],
  cargoItemsByKey: Record<string, CargoItemFormValue>,
  allocationHints: AllocationHintMap,
  existingCargoItems: SharedTransportCargoItem[] = [],
): CargoTableRow[] {
  return selectedOrders.flatMap((order) =>
    order.products.map((product) => {
      const key = buildCargoKey(order.id, product.id);
      const hint = allocationHints[product.id];
      const allocatedElsewhere = hint ? Math.max(hint.allocatedQuantity, 0) : 0;
      const formItem = cargoItemsByKey[key] || {};
      const existingCargoItem = existingCargoItems.find((item) =>
        item.orderId === order.id && item.orderProductId === product.id
      );

      return {
        key,
        id: formItem.id || existingCargoItem?.id,
        orderId: order.id,
        orderProductId: product.id,
        orderNumber: order.displayOrderNumber || order.orderNumber,
        productName: getTransportOrderProductDisplayName(product),
        quantity: toNumberValue(formItem.quantity, existingCargoItem?.quantity ?? product.quantity ?? 0),
        availableQuantity: product.quantity ?? 0,
        allocatedElsewhere,
        unit: product.unit || 'pcs',
        goodsValueTotal: toNumberValue(formItem.goodsValueTotal, existingCargoItem?.goodsValueTotal ?? 0),
        manualAllocatedTransportCost: toNullableNumberValue(
          formItem.manualAllocatedTransportCost,
          existingCargoItem?.manualAllocatedTransportCost ?? null,
        ),
        grossWeight: product.grossWeight,
        width: product.width,
        height: product.height,
        length: product.length,
        thickness: product.thickness,
        // Package-based LDM prefill: operator form value wins, then the cargo
        // snapshot (edit mode), then the product catalog default. This mirrors
        // the dims snapshot pattern; packaging does not recompute from quantity.
        piecesPerPackage: toNullableNumberValue(
          formItem.piecesPerPackage,
          existingCargoItem?.piecesPerPackage ?? product.piecesPerPackage ?? null,
        ),
        packageLength: toNullableNumberValue(
          formItem.packageLength,
          existingCargoItem?.packageLength ?? product.packageLength ?? null,
        ),
        packageWidth: toNullableNumberValue(
          formItem.packageWidth,
          existingCargoItem?.packageWidth ?? product.packageWidth ?? null,
        ),
        packageStackLayers: toNullableNumberValue(
          formItem.packageStackLayers,
          existingCargoItem?.packageStackLayers ?? product.packageStackLayers ?? null,
        ),
        // F2: manual LDM override. Unlike the packaging fields above, this must
        // NOT fall back to the cargo snapshot here — the seed effect in
        // SharedTransportForm.tsx already copies the snapshot into the form
        // field ONCE, on mount (existingCargoItem?.manualLdm ?? null). From
        // that point on the FORM FIELD is the single source of truth: reading
        // formItem.manualLdm directly (never falling back to the snapshot
        // again) means the operator emptying the InputNumber correctly clears
        // the override (empty -> null), instead of this row silently
        // re-reverting to the old persisted value on every re-render — the
        // "empty field still saves the previous override" friction bug.
        manualLdm: toFiniteOrNull(formItem.manualLdm),
      };
    })
  );
}

// Stable dependency-array key for effects that recompute cargo-derived totals
// (packages/weight/volume/dimensions summary) from a fresh CargoTableRow[]
// snapshot. MUST include every metrics input the summary can change on,
// package-based LDM's four packaging fields AND the manual LDM override (F2)
// included — a key missing one of them lets a memoized effect skip re-running
// after an edit to that field, leaving stale derived values in the form
// (browser-regression MED-3: the `dimensions` string kept the pre-edit
// "— LDM" after a packaging-only edit via the per-line popover, because the
// old key only tracked quantity).
export function buildCargoRowsMetricsKey(rows: CargoTableRow[]): string {
  return JSON.stringify(rows.map((row) => ({
    key: row.key,
    quantity: row.quantity,
    piecesPerPackage: row.piecesPerPackage,
    packageLength: row.packageLength,
    packageWidth: row.packageWidth,
    packageStackLayers: row.packageStackLayers,
    manualLdm: row.manualLdm,
  })));
}

// JULIET (MED "stale Pozostało hint"): how much of an order product's
// quantity remains available to load onto ANOTHER transport, once both
// what's already loaded elsewhere AND what the operator is CURRENTLY typing
// into this transport's own "Do załadunku" field are accounted for. Extracted
// as a pure function so the "Pozostało" cell hint (TransportOrdersCargoSection)
// and the "overflow" guard on the loaded-quantity cell can share one formula
// and never disagree about what "remaining" means. Never negative — a
// mid-typing quantity that already exceeds availability shows 0 remaining,
// not a misleading negative number (the overflow guard is what blocks save).
export function deriveRemainingElsewhereQuantity(
  availableQuantity: number,
  allocatedElsewhere: number,
  currentQuantity: number,
): number {
  return Math.max(availableQuantity - allocatedElsewhere - (Number(currentQuantity) || 0), 0);
}

export function toNumberValue(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

export function toNullableNumberValue(value: unknown, fallback: number | null): number | null {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

// Like toNullableNumberValue, but with NO fallback: empty/undefined/invalid
// always resolves to null. For fields where the FORM FIELD ITSELF is already
// the single source of truth (seeded once from the snapshot on mount — see
// manualLdm in buildCargoRows above) — falling back to a snapshot value here
// would silently resurrect a value the operator just explicitly cleared.
export function toFiniteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}
